<script lang="ts">
  /**
   * TaskDetailPopover - A popover for viewing and editing task details.
   *
   * Opens on task click, positioned smartly to not block the task.
   *
   * Layout (top to bottom):
   * - Project breadcrumb (click → move to another project), ⋯ more actions, close
   * - Completion checkbox beside an auto-expanding title, then notes
   * - Property rows: Priority, Tags, Now
   * - Footer: created/completed dates and Won't do (or Reopen)
   *
   * The ⋯ menu holds the less frequent actions: Duplicate, Move to project,
   * Convert to project, Delete. "Move to project…" drills into a project search
   * inside the same floating menu (no extra layer); the breadcrumb opens that
   * search directly. Those actions run through lib/taskActions.ts, which works from
   * the shared task cache, so parents only wire the core callbacks below.
   *
   * Features:
   * - Auto-expanding title and notes fields (no fixed height)
   * - Notes only scrolls when popover would exceed viewport
   * - Borderless input styling with placeholder text
   * - Unsaved title/notes/tag edits are saved on close and before any action
   *
   * On mobile (`$viewport.isMobile`) the same content opens as a bottom sheet
   * (ResponsiveOverlay → MobileSheet, half/full detents) instead of an anchored
   * popover: 44px+ controls, a segmented priority control, and a visible action
   * row (Move to… / Won't do / Delete). The ⋯ menu and project search then
   * replace the sheet's content in place instead of floating over it.
   */
  import { tick, type Snippet } from 'svelte';
  import { isCoarsePointer, viewport } from '../lib/stores/viewport';
  import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
  import type { Task, TaskPriority } from '../lib/types';
  import type { SheetAction } from '../lib/sheetActions';
  import { categoryIcons } from '../lib/utils';
  import {
    convertTaskToProject,
    duplicateTaskFromDetails,
    moveTaskFromDetails,
  } from '../lib/taskActions';
  import Icon from './Icon.svelte';
  import TaskCopyActionRow from './TaskCopyActionRow.svelte';
  import MoveToProjectPanel from './MoveToProjectPanel.svelte';
  import NowToggle from './NowToggle.svelte';
  import RecentMoveMenuItem from './RecentMoveMenuItem.svelte';
  import ResponsiveOverlay from './ResponsiveOverlay.svelte';
  import TagPicker from './TagPicker.svelte';

  interface Props {
    task: Task | null;
    anchorEl: HTMLElement | null;
    /** The task's project, for the breadcrumb. Falls back to a generic label when unknown. */
    project?: { title: string; category?: string | null } | null;
    onClose: () => void;
    onUpdate: (task: Task, updates: Partial<Task>) => void;
    onDelete: (task: Task, pendingUpdates?: Partial<Task>) => void;
    onMarkWontDo: (task: Task, pendingUpdates?: Partial<Task>) => void;
    onToggleComplete: (task: Task, pendingUpdates?: Partial<Task>) => void;
    /**
     * Open straight on the ⋯ menu (a list row's ⋯ on mobile), so a row's menu and
     * the task's own ⋯ are one and the same. Applies each time a task opens.
     */
    initialMenu?: 'actions' | null;
    /**
     * Extra rows at the top of the ⋯ menu, e.g. a list row's Move up / Move down.
     * Choosing one saves pending edits, closes the popover, then runs it.
     */
    menuActions?: SheetAction[];
    /**
     * Adds a "Working on" switch to the properties (pin / unpin the task). Only the
     * Timeline Tasks list passes it, so the swipe row's Pin action also lives here.
     */
    onToggleActive?: (task: Task) => void;
  }

  let {
    task,
    anchorEl,
    project = null,
    onClose,
    onUpdate,
    onDelete,
    onMarkWontDo,
    onToggleComplete,
    initialMenu = null,
    menuActions = [],
    onToggleActive,
  }: Props = $props();

  // Bottom sheet instead of an anchored popover (mobile)
  let sheetMode = $derived($viewport.isMobile);

  // Local editing state
  let editingTitle = $state('');
  let editingNotes = $state('');
  let editingTags = $state<string[]>([]);
  let titleInputEl: HTMLTextAreaElement | undefined = $state();
  let notesInputEl: HTMLTextAreaElement | undefined = $state();
  let previousTask = $state<Task | null>(null);

  // Max height for notes (calculated based on viewport space, applied via CSS)
  let notesMaxHeight = $state<string | null>(null);
  // Current height for notes (tracks content size)
  let notesHeight = $state<string>('auto');

  // Track which task ID was last opened (non-reactive to avoid effect cycles)
  let lastOpenedTaskId: string | null = null;

  // Sync local state when task changes, and save when closing
  // eslint-disable-next-line momentium/no-unguarded-effect -- lifecycle: init edit fields on open, auto-save on close (task→null transition)
  $effect(() => {
    if (task) {
      const isDifferentTask = lastOpenedTaskId !== task.id;
      if (isDifferentTask) {
        lastOpenedTaskId = task.id;
        editingTitle = task.title;
        editingNotes = task.notes || '';
        editingTags = (task.tags ?? []).map((tag) => tag.name);
        notesMaxHeight = null; // Reset constraint for new task
        notesHeight = 'auto';
        anchorSnapshot = null; // Reset position snapshot for new task
        menu = initialMenu;
      }
      previousTask = task;
    } else if (previousTask) {
      // Task became null - save any pending changes.
      const updates = getPendingUpdatesFor(previousTask);
      if (updates) onUpdate(previousTask, updates);
      previousTask = null;
      lastOpenedTaskId = null;
      menu = null;
    }
  });

  // Auto-size the title when popover opens (focus happens once it is positioned,
  // see focusOnOpen)
  // eslint-disable-next-line momentium/no-unguarded-effect -- DOM measurement: must auto-size title input after tick() when popover opens
  $effect(() => {
    if (task && titleInputEl) {
      tick().then(() => {
        autoResizeTextarea(titleInputEl);
        if (titleInputEl) titleInputEl.scrollTop = 0;
      });
    }
  });

  // Initial focus. Runs after the first positioning of each opened task: until
  // then the popover is visibility: hidden and can't take focus. On touch, focus
  // the popover itself so screen readers start inside it without the keyboard
  // covering it; otherwise the title, cursor at the start so it reads from the top.
  function focusOnOpen() {
    if (isCoarsePointer()) {
      floatingEl?.focus({ preventScroll: true });
    } else if (titleInputEl) {
      titleInputEl.focus();
      titleInputEl.setSelectionRange(0, 0);
      titleInputEl.scrollTop = 0;
    }
  }

  // Size notes field when popover opens (separate effect to ensure notesInputEl is bound)
  // eslint-disable-next-line momentium/no-unguarded-effect -- DOM measurement: must measure and auto-size notes textarea after tick() when popover opens
  $effect(() => {
    if (task && notesInputEl) {
      tick().then(() => {
        updateNotesSize();
      });
    }
  });

  // Auto-resize textarea to fit content (respects CSS max-height)
  function autoResizeTextarea(el: HTMLTextAreaElement | undefined) {
    if (!el) return;
    el.style.height = 'auto';
    const naturalHeight = el.scrollHeight;
    el.style.height = naturalHeight + 'px';
    // Enable scrolling if content exceeds max-height (set in CSS)
    const maxHeight = parseFloat(getComputedStyle(el).maxHeight);
    el.style.overflow = !isNaN(maxHeight) && naturalHeight > maxHeight ? 'auto' : 'hidden';
  }

  // Handle title input
  function handleTitleInput(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    editingTitle = target.value;
    autoResizeTextarea(target);
  }

  // Handle notes input
  function handleNotesInput(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    editingNotes = target.value;
    updateNotesSize();
  }

  // Calculate max height for notes based on viewport (called once after positioning)
  function calculateNotesMaxHeight() {
    if (!floatingEl || !notesInputEl) return;

    const notesRect = notesInputEl.getBoundingClientRect();
    const viewportBottom = window.innerHeight;
    const bottomPadding = 16;
    const contentBelowNotes = 150; // property rows + footer

    const availableSpace = viewportBottom - notesRect.top - bottomPadding - contentBelowNotes;

    // Set a max-height if we have reasonable space (at least 60px)
    if (availableSpace > 60) {
      notesMaxHeight = `${availableSpace}px`;
    } else {
      notesMaxHeight = '60px'; // minimum usable height
    }
  }

  // Auto-resize notes textarea (respects max-height constraint)
  function updateNotesSize() {
    if (!notesInputEl) return;

    // Set height to 0 to force all content into scrollable area for accurate measurement
    notesInputEl.style.height = '0px';
    notesInputEl.style.maxHeight = 'none';
    notesInputEl.style.overflow = 'hidden';

    // scrollHeight now reflects full content height including soft wraps
    const naturalHeight = notesInputEl.scrollHeight;

    // Set final height directly on DOM (no clearing - prevents visual flash)
    // Important: We set styles directly rather than clearing and relying on Svelte's
    // binding to re-render, because the brief moment with empty styles causes a
    // visible "flash" where the textarea collapses to default height.
    notesInputEl.style.height = `${naturalHeight}px`;
    notesInputEl.style.maxHeight = notesMaxHeight || '';
    notesInputEl.style.overflow = notesMaxHeight ? 'auto' : 'hidden';

    // Update state for consistency (Svelte binding will match what we set)
    notesHeight = `${naturalHeight}px`;
  }

  // Floating UI positioning
  let floatingEl: HTMLDivElement | undefined = $state();
  let floatingStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');
  let autoUpdateCleanup: (() => void) | null = null;
  // Snapshot of anchor position when popover first opens. If the anchor moves
  // (e.g., task reorders due to priority change), we freeze the popover in place.
  let anchorSnapshot: { top: number; left: number } | null = null;

  async function updatePosition(anchor: HTMLElement, floating: HTMLDivElement) {
    // Skip repositioning if the anchor element was removed from the DOM
    // (e.g., task moved to a different priority group). The popover keeps its
    // last good position until the parent provides a new connected anchor.
    if (!anchor.isConnected) return;

    // If the anchor moved (task reordered in list), freeze popover position.
    // Only allow repositioning if the anchor is roughly where it started.
    const rect = anchor.getBoundingClientRect();
    if (anchorSnapshot) {
      const movedY = Math.abs(rect.top - anchorSnapshot.top);
      const movedX = Math.abs(rect.left - anchorSnapshot.left);
      // Allow small drift (scroll adjustments, subpixel), but not task reordering
      if (movedY > 5 || movedX > 5) return;
    }

    const result = await computePosition(anchor, floating, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [
        offset(4),
        flip({
          fallbackPlacements: ['top-start', 'bottom-end', 'top-end', 'right-start', 'left-start'],
        }),
        shift({ padding: 8 }),
      ],
    });
    floatingStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;

    // Capture initial anchor position on first successful positioning
    if (!anchorSnapshot) {
      anchorSnapshot = { top: rect.top, left: rect.left };
      tick().then(focusOnOpen);
    }

    // Only calculate max height once (when notesMaxHeight is null)
    // Don't recalculate on every autoUpdate cycle
    if (notesMaxHeight === null) {
      tick().then(calculateNotesMaxHeight);
    }
  }

  // Set up positioning when popover opens
  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate lifecycle: must call autoUpdate() on mount and return cleanup
  $effect(() => {
    if (autoUpdateCleanup) {
      autoUpdateCleanup();
      autoUpdateCleanup = null;
    }

    if (task && anchorEl && floatingEl && !sheetMode) {
      autoUpdateCleanup = autoUpdate(anchorEl, floatingEl, () => {
        if (floatingEl && anchorEl) updatePosition(anchorEl, floatingEl);
      });
    }

    return () => {
      if (autoUpdateCleanup) {
        autoUpdateCleanup();
        autoUpdateCleanup = null;
      }
    };
  });

  // ── More-actions menu ────────────────────────────────────────────────────
  // 'actions'   — the ⋯ menu's item list
  // 'move'      — drilled in from "Move to project…" (back returns to 'actions')
  // 'move-crumb'— opened straight from the project breadcrumb (no back step)
  type MenuView = 'actions' | 'move' | 'move-crumb';
  let menu = $state<MenuView | null>(null);
  let menuEl: HTMLDivElement | undefined = $state();
  let moreBtnEl: HTMLButtonElement | undefined = $state();
  let crumbBtnEl: HTMLButtonElement | undefined = $state();
  let menuStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');
  let movePanel: MoveToProjectPanel | undefined = $state();

  // Anchor the menu to whichever control opened it; keep it attached while the
  // popover repositions (the popover itself can move as notes grow).
  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate lifecycle for the actions menu, returns cleanup
  $effect(() => {
    if (!menu || !menuEl || sheetMode) return;
    const anchor = menu === 'move-crumb' ? crumbBtnEl : moreBtnEl;
    if (!anchor) return;
    const floating = menuEl;
    const placement = menu === 'move-crumb' ? 'bottom-start' : 'bottom-end';
    return autoUpdate(anchor, floating, async () => {
      const result = await computePosition(anchor, floating, {
        strategy: 'fixed',
        placement,
        middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
      });
      const wasHidden = menuStyle.includes('hidden');
      menuStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;
      // Nothing can take focus while the menu is still hidden, so focus once it's shown:
      // the project search, or the menu itself (for arrow-key navigation).
      if (wasHidden) {
        tick().then(() => (movePanel ? movePanel.focus() : menuEl?.focus({ preventScroll: true })));
      }
    });
  });

  // Reset position between openings so the menu never flashes at a stale spot.
  // eslint-disable-next-line momentium/no-unguarded-effect -- resets menu positioning state when the menu closes
  $effect(() => {
    if (!menu) menuStyle = 'position: fixed; left: 0; top: 0; visibility: hidden;';
  });

  function toggleMoreMenu() {
    menu = menu === 'actions' || menu === 'move' ? null : 'actions';
  }

  function toggleCrumbPicker() {
    menu = menu === 'move-crumb' ? null : 'move-crumb';
  }

  function closeMenu() {
    menu = null;
  }

  // Arrow-key navigation between the actions menu's items.
  function handleMenuKeydown(e: KeyboardEvent) {
    if (menu !== 'actions' || !menuEl) return;
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(menuEl.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === 'ArrowDown'
        ? (current + 1) % items.length
        : (current - 1 + items.length) % items.length;
    items[next].focus();
  }

  function handlePopoverMousedown(e: MouseEvent) {
    e.stopPropagation();
    // Clicking anywhere else in the popover dismisses an open menu.
    if (!menu) return;
    const target = e.target as Node;
    if (menuEl?.contains(target) || moreBtnEl?.contains(target) || crumbBtnEl?.contains(target)) {
      return;
    }
    closeMenu();
  }

  // Close on Escape (an open menu closes first)
  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (menu) {
        closeMenu();
        return;
      }
      saveAndClose();
    }
  }

  // Close on click outside (but not when clicking the anchor - let parent toggle handle that)
  function handleClickOutside(e: MouseEvent) {
    if (floatingEl && !floatingEl.contains(e.target as Node)) {
      // If clicking the anchor element, don't close here - let parent's toggle handle it
      if (anchorEl?.contains(e.target as Node)) {
        return;
      }
      saveAndClose();
    }
  }

  // (The bottom sheet handles Esc and backdrop taps itself.)
  // eslint-disable-next-line momentium/no-unguarded-effect -- document-level event listeners with conditional setup/teardown and cleanup
  $effect(() => {
    if (task && !sheetMode) {
      document.addEventListener('keydown', handleKeydown);
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('keydown', handleKeydown);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  });

  function saveAndClose() {
    if (task) {
      const pending = getPendingUpdates();
      if (pending) {
        onUpdate(task, pending);
      }
      previousTask = null; // Prevent effect from double-saving
    }
    onClose();
  }

  function handleSetPriority(priority: TaskPriority) {
    if (task && task.priority !== priority) {
      onUpdate(task, { priority });
    }
  }

  // Get unsaved title, notes, and tag changes. `tag_names` is the write-only
  // request shape; task responses use `tags`, which is why the comparison is
  // against tag names here.
  function getPendingUpdatesFor(currentTask: Task): Partial<Task> | undefined {
    const updates: Partial<Task> = {};
    const newTitle = editingTitle.trim();
    const newNotes = editingNotes.trim() || null;
    const currentTagNames = (currentTask.tags ?? []).map((tag) => tag.name);
    if (newTitle && newTitle !== currentTask.title) updates.title = newTitle;
    if (newNotes !== (currentTask.notes || null)) updates.notes = newNotes;
    if (editingTags.join('\u0000') !== currentTagNames.join('\u0000')) {
      updates.tag_names = editingTags;
    }
    return Object.keys(updates).length > 0 ? updates : undefined;
  }

  function getPendingUpdates(): Partial<Task> | undefined {
    return task ? getPendingUpdatesFor(task) : undefined;
  }

  // What "Copy as" copies: the task with any unsaved title/notes/tag edits, so
  // the copy matches what's on screen. Copying keeps the popover open.
  let copyTask = $derived.by((): Task | null => {
    if (!task) return null;
    const { tag_names, ...pending } = getPendingUpdatesFor(task) ?? {};
    return {
      ...task,
      ...pending,
      ...(tag_names ? { tags: tag_names.map((name) => ({ id: name, name })) } : {}),
    };
  });

  /**
   * Close the popover and hand the task plus any unsaved edits to an action.
   * The action owns persisting those edits, so the close-time auto-save is skipped.
   */
  function closeThen(action: (task: Task, pending?: Partial<Task>) => void) {
    if (!task) return;
    const current = task;
    const pending = getPendingUpdates();
    previousTask = null;
    menu = null;
    action(current, pending);
    onClose();
  }

  function handleDelete() {
    closeThen((t, pending) => onDelete(t, pending));
  }

  function handleMarkWontDo() {
    closeThen((t, pending) => onMarkWontDo(t, pending));
  }

  function handleToggleComplete() {
    closeThen((t, pending) => onToggleComplete(t, pending));
  }

  function handleDuplicate() {
    closeThen((t, pending) => duplicateTaskFromDetails(t, pending));
  }

  function handleConvert() {
    closeThen((t, pending) => convertTaskToProject(t, pending));
  }

  function handleMenuAction(action: SheetAction) {
    saveAndClose();
    action.onSelect();
  }

  function handleMoveTo(targetProjectId: string, targetProjectTitle: string) {
    closeThen((t, pending) => moveTaskFromDetails(t, targetProjectId, targetProjectTitle, pending));
  }

  // `short` fits the sheet's four equal segments at 360px
  const priorityOptions: { value: TaskPriority; label: string; short: string }[] = [
    { value: 'none', label: 'None', short: 'None' },
    { value: 'low', label: 'Low', short: 'Low' },
    { value: 'medium', label: 'Medium', short: 'Med' },
    { value: 'high', label: 'High', short: 'High' },
  ];

  /** "Sep 5", or "Sep 5, 2025" outside the current year — keeps the footer on one line. */
  function formatDate(value: string): string {
    const date = new Date(value);
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
  }

  let isOpenTask = $derived(task?.status === 'todo');
  let crumbIcon = $derived(project?.category ? categoryIcons[project.category] : null);
</script>

<ResponsiveOverlay
  open={!!task}
  onClose={saveAndClose}
  label="Task details"
  detents={['half', 'full']}
  initialDetent="half"
  --mobile-sheet-z="200"
>
  {#snippet desktop(content: Snippet)}
    {#if task}
      <div
        class="task-popover"
        style={floatingStyle}
        bind:this={floatingEl}
        role="dialog"
        aria-label="Task details"
        tabindex="-1"
      >
        {@render content()}
      </div>
    {/if}
  {/snippet}

  {#if task}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="task-detail" class:sheet={sheetMode} onmousedown={handlePopoverMousedown}>
      <!-- Top bar: project breadcrumb + actions (also the sheet's drag handle) -->
      <div class="popover-top" data-sheet-drag-handle>
        <button
          type="button"
          class="project-crumb"
          class:open={menu === 'move-crumb'}
          bind:this={crumbBtnEl}
          onclick={toggleCrumbPicker}
          title="Move to another project"
          aria-haspopup="dialog"
          aria-expanded={menu === 'move-crumb'}
        >
          {#if crumbIcon}<span class="crumb-emoji">{crumbIcon}</span>{/if}
          <span class="crumb-title">{project?.title ?? 'Move to project'}</span>
          <Icon name="chevron-down" size={12} class="crumb-chevron" />
        </button>
        <span class="top-spacer"></span>
        <button
          type="button"
          class="icon-btn"
          class:pressed={menu === 'actions' || menu === 'move'}
          bind:this={moreBtnEl}
          onclick={toggleMoreMenu}
          title="More actions"
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={menu === 'actions' || menu === 'move'}
        >
          <Icon name="more" size={16} />
        </button>
        <button
          type="button"
          class="icon-btn close-btn"
          onclick={saveAndClose}
          title="Close (Esc)"
          aria-label="Close"
          data-sheet-close
        >
          <Icon name="close" size={14} />
        </button>
      </div>

      <!-- In the sheet, an open menu replaces the task body instead of floating over it -->
      <div class="task-body" hidden={sheetMode && !!menu}>
        <!-- Checkbox + title, notes -->
        <div class="task-main">
          <button
            type="button"
            class="complete-check"
            class:done={task.status === 'done'}
            class:wont-do={task.status === 'wont_do'}
            role="checkbox"
            aria-checked={task.status === 'done'}
            aria-label={isOpenTask ? 'Mark done' : 'Reopen task'}
            title={isOpenTask ? 'Mark done' : 'Reopen'}
            onclick={handleToggleComplete}
          >
            <Icon
              name={task.status === 'wont_do' ? 'wont-do' : 'check'}
              size={12}
              strokeWidth={2}
            />
          </button>
          <textarea
            id="task-title"
            class="title-input"
            class:closed={!isOpenTask}
            aria-label="Task title"
            data-sheet-autofocus
            bind:this={titleInputEl}
            bind:value={editingTitle}
            placeholder="Type your task..."
            rows="1"
            oninput={handleTitleInput}
            onkeydown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                saveAndClose();
              }
            }}></textarea>
          <textarea
            id="task-notes"
            class="notes-input"
            aria-label="Notes"
            bind:this={notesInputEl}
            bind:value={editingNotes}
            placeholder="Add notes..."
            rows="1"
            oninput={handleNotesInput}
            style={`height: ${notesHeight};${notesMaxHeight ? ` max-height: ${notesMaxHeight}; overflow-y: auto;` : ''}`}
          ></textarea>
        </div>

        <!-- Properties -->
        <div class="task-props">
          <span class="prop-label"><Icon name="priority" size={13} />Priority</span>
          <div class="priority-picker" role="radiogroup" aria-label="Priority">
            {#each priorityOptions as opt (opt.value)}
              <button
                type="button"
                class="priority-option priority-{opt.value}"
                class:active={task.priority === opt.value}
                role="radio"
                aria-checked={task.priority === opt.value}
                aria-label={opt.label}
                onclick={() => handleSetPriority(opt.value)}
                title={opt.label}
              >
                {#if task.priority === opt.value && opt.value !== 'none'}
                  <span class="priority-dot"></span>
                {/if}
                {sheetMode ? opt.short : opt.label}
              </button>
            {/each}
          </div>

          <span class="prop-label"><Icon name="tag" size={13} />Tags</span>
          <div class="prop-value">
            <TagPicker
              variant="inline"
              selected={editingTags}
              onChange={(names) => (editingTags = names)}
            />
          </div>

          <span class="prop-label"><Icon name="now" size={13} />Now</span>
          <div class="prop-value">
            <NowToggle {task} variant="switch" />
          </div>

          {#if onToggleActive && isOpenTask}
            <span class="prop-label"><span class="prop-emoji" aria-hidden="true">📌</span>Pin</span>
            <div class="prop-value">
              <button
                type="button"
                class="working-on-switch"
                class:on={task.isActive}
                role="switch"
                aria-checked={task.isActive}
                aria-label="Working on"
                onclick={() => onToggleActive(task)}
              >
                <span class="working-on-track" aria-hidden="true"></span>
                <span class="working-on-label">
                  {task.isActive ? 'In Working on' : 'Add to Working on'}
                </span>
              </button>
            </div>
          {/if}
        </div>

        <!-- Sheet: primary actions up front (no ⋯ hunting); Move to… stands in for drag-to-project -->
        {#if sheetMode}
          <div class="sheet-actions">
            <button type="button" class="sheet-action" onclick={() => (menu = 'move-crumb')}>
              <Icon name="move-to-project" size={18} />Move to…
            </button>
            {#if isOpenTask}
              <button type="button" class="sheet-action" onclick={handleMarkWontDo}>
                <Icon name="wont-do" size={18} />Won't do
              </button>
            {:else}
              <button type="button" class="sheet-action" onclick={handleToggleComplete}>
                <Icon name="reopen" size={18} />Reopen
              </button>
            {/if}
            <button type="button" class="sheet-action danger" onclick={handleDelete}>
              <Icon name="trash" size={18} />Delete
            </button>
          </div>
        {/if}

        <!-- Footer -->
        <div class="popover-footer">
          <span class="meta">
            Created {formatDate(task.createdAt)}
            {#if task.completedAt}
              · {task.status === 'wont_do' ? 'Closed' : 'Completed'} {formatDate(task.completedAt)}
            {/if}
          </span>
          {#if !sheetMode}
            <span class="top-spacer"></span>
            {#if isOpenTask}
              <button type="button" class="footer-btn" onclick={handleMarkWontDo}>
                <Icon name="wont-do" size={13} />Won't do
              </button>
            {:else}
              <button type="button" class="footer-btn" onclick={handleToggleComplete}>
                <Icon name="reopen" size={13} />Reopen
              </button>
            {/if}
          {/if}
        </div>
      </div>

      <!-- More actions menu / project search (rendered inside the popover so outside-click logic treats it as part of it) -->
      {#if menu}
        <div
          class="popover-menu"
          class:picker={menu !== 'actions'}
          style={sheetMode ? undefined : menuStyle}
          bind:this={menuEl}
          role={menu === 'actions' ? 'menu' : 'dialog'}
          aria-label={menu === 'actions' ? 'More actions' : 'Move to project'}
          tabindex="-1"
          onkeydown={handleMenuKeydown}
        >
          {#if menu === 'actions'}
            {#if menuActions.length > 0}
              {#each menuActions as action (action.label)}
                <button
                  type="button"
                  class="menu-item"
                  class:danger={action.danger}
                  role="menuitem"
                  onclick={() => handleMenuAction(action)}
                >
                  {#if action.icon}<Icon name={action.icon} />{/if}{action.label}
                </button>
              {/each}
              <div class="menu-divider"></div>
            {/if}
            <button type="button" class="menu-item" role="menuitem" onclick={handleDuplicate}>
              <Icon name="duplicate" />Duplicate task
            </button>
            {#if copyTask}
              <TaskCopyActionRow
                task={copyTask}
                itemRole="menuitem"
                onDone={closeMenu}
                --copy-row-font-size={sheetMode ? '1rem' : '0.75rem'}
              />
            {/if}
            <div class="menu-divider"></div>
            <div class="recent-move-slot">
              <RecentMoveMenuItem currentProjectId={task.projectId} onMove={handleMoveTo} />
            </div>
            <button type="button" class="menu-item" role="menuitem" onclick={() => (menu = 'move')}>
              <Icon name="move-to-project" />Move to project…
              <Icon name="chevron-right" size={12} class="menu-trail" />
            </button>
            <button type="button" class="menu-item" role="menuitem" onclick={handleConvert}>
              <Icon name="convert" />Convert to project
            </button>
            <div class="menu-divider"></div>
            <button type="button" class="menu-item danger" role="menuitem" onclick={handleDelete}>
              <Icon name="trash" />Delete task
            </button>
          {:else}
            <MoveToProjectPanel
              bind:this={movePanel}
              excludeProjectId={task.projectId}
              onSelect={handleMoveTo}
              onBack={menu === 'move'
                ? () => (menu = 'actions')
                : sheetMode
                  ? closeMenu
                  : undefined}
              onEscape={closeMenu}
              autofocus={!sheetMode || !isCoarsePointer()}
              title={menu === 'move' || sheetMode ? 'Move to project' : null}
              placeholder={menu === 'move' ? 'Search projects...' : 'Move to project…'}
            />
          {/if}
        </div>
      {/if}
    </div>
  {/if}
</ResponsiveOverlay>

<style>
  .task-popover {
    outline: none;
    z-index: 200;
    width: 320px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow:
      0 10px 25px rgba(0, 0, 0, 0.15),
      0 4px 10px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
  }

  /* ── Top bar ───────────────────────────────────────── */
  .popover-top {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.5rem 0.375rem 0 0.75rem;
  }

  .top-spacer {
    flex: 1;
  }

  .project-crumb {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    max-width: 14rem;
    height: 1.375rem;
    margin-left: -0.25rem;
    padding: 0 0.375rem 0 0.25rem;
    font-family: inherit;
    font-size: 0.71875rem;
    color: #6b7280;
    background: none;
    border: none;
    border-radius: 0.3125rem;
    cursor: pointer;
    transition:
      background-color 0.15s,
      color 0.15s;
  }

  .project-crumb:hover,
  .project-crumb.open {
    color: #374151;
    background: #f3f4f6;
  }

  .project-crumb.open {
    background: #e5e7eb;
  }

  .crumb-emoji {
    font-size: 0.6875rem;
    flex-shrink: 0;
  }

  .crumb-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .project-crumb :global(.crumb-chevron) {
    color: #9ca3af;
  }

  .icon-btn {
    width: 1.5rem;
    height: 1.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #6b7280;
    background: none;
    border: none;
    border-radius: 0.3125rem;
    cursor: pointer;
    transition:
      background-color 0.15s,
      color 0.15s;
  }

  .icon-btn:hover {
    color: #1f2937;
    background: #f3f4f6;
  }

  .icon-btn.pressed {
    color: #1f2937;
    background: #e5e7eb;
  }

  .project-crumb:focus-visible,
  .icon-btn:focus-visible,
  .complete-check:focus-visible,
  .priority-option:focus-visible,
  .footer-btn:focus-visible,
  .menu-item:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  /* ── Checkbox + title + notes ──────────────────────── */
  .task-main {
    display: grid;
    grid-template-columns: 1.375rem 1fr;
    column-gap: 0.375rem;
    padding: 0.375rem 0.75rem 0.625rem;
  }

  .complete-check {
    width: 1.125rem;
    height: 1.125rem;
    margin-top: 0.4375rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    background: white;
    border: 1.5px solid #d1d5db;
    border-radius: 999px;
    cursor: pointer;
    transition:
      border-color 0.15s,
      color 0.15s,
      background-color 0.15s;
  }

  .complete-check:hover {
    color: #16a34a;
    border-color: #16a34a;
  }

  .complete-check.done {
    color: white;
    background: #16a34a;
    border-color: #16a34a;
  }

  .complete-check.wont-do {
    color: white;
    background: #9ca3af;
    border-color: #9ca3af;
  }

  .complete-check.done:hover,
  .complete-check.wont-do:hover {
    background: #6b7280;
    border-color: #6b7280;
  }

  .title-input {
    width: 100%;
    padding: 0.25rem 0.375rem;
    font-family: inherit;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #1f2937;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    outline: none;
    resize: none;
    overflow: hidden;
    line-height: 1.4;
    max-height: 6rem; /* ~4-5 lines, then scrolls */
    transition: background-color 0.15s;
  }

  .title-input.closed {
    color: #6b7280;
  }

  .notes-input {
    grid-column: 2;
    width: 100%;
    margin-top: 0.125rem;
    padding: 0.25rem 0.375rem;
    font-family: inherit;
    font-size: 0.8125rem;
    color: #4b5563;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    outline: none;
    resize: none;
    overflow: hidden;
    line-height: 1.5;
    transition: background-color 0.15s;
  }

  .title-input:hover,
  .notes-input:hover {
    background: rgba(0, 0, 0, 0.02);
  }

  .title-input:focus,
  .notes-input:focus {
    background: rgba(59, 130, 246, 0.04);
  }

  .title-input::placeholder,
  .notes-input::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  /* ── Properties ────────────────────────────────────── */
  .task-props {
    display: grid;
    grid-template-columns: 4.25rem 1fr;
    align-items: center;
    row-gap: 0.25rem;
    margin: 0 0.5rem;
    padding: 0.5rem 0.25rem 0.5rem 0.375rem;
    border-top: 1px solid #f3f4f6;
  }

  .prop-label {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    min-height: 1.625rem;
    font-size: 0.71875rem;
    color: #6b7280;
  }

  .prop-label :global(.icon) {
    color: #9ca3af;
  }

  .prop-value {
    min-width: 0;
    min-height: 1.625rem;
    display: flex;
    align-items: center;
  }

  .priority-picker {
    display: flex;
    gap: 0.0625rem;
  }

  .priority-option {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    height: 1.375rem;
    padding: 0 0.4375rem;
    font-family: inherit;
    font-size: 0.71875rem;
    color: #6b7280;
    background: none;
    border: 1px solid transparent;
    border-radius: 999px;
    cursor: pointer;
    transition:
      background-color 0.15s,
      color 0.15s;
  }

  .priority-option:hover {
    color: #374151;
    background: #f3f4f6;
  }

  .priority-option.active {
    font-weight: 500;
  }

  .priority-dot {
    width: 0.4375rem;
    height: 0.4375rem;
    border-radius: 999px;
    background: currentColor;
  }

  .priority-option.active.priority-none {
    color: #374151;
    background: #f3f4f6;
    border-color: #e5e7eb;
  }

  .priority-option.active.priority-low {
    color: #1d4ed8;
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  .priority-option.active.priority-medium {
    color: #c2410c;
    background: #fff7ed;
    border-color: #fed7aa;
  }

  .priority-option.active.priority-high {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
  }

  /* ── Footer ────────────────────────────────────────── */
  .popover-footer {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.5rem 0.375rem 0.875rem;
    border-top: 1px solid #f3f4f6;
  }

  .meta {
    font-size: 0.65625rem;
    color: #9ca3af;
  }

  .footer-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    height: 1.5rem;
    padding: 0 0.5rem;
    font-family: inherit;
    font-size: 0.71875rem;
    color: #6b7280;
    background: none;
    border: none;
    border-radius: 0.3125rem;
    cursor: pointer;
    white-space: nowrap;
    transition:
      background-color 0.15s,
      color 0.15s;
  }

  .footer-btn:hover {
    color: #374151;
    background: #f3f4f6;
  }

  /* ── Menu ──────────────────────────────────────────── */
  .popover-menu {
    z-index: 210;
    width: 13.25rem;
    padding: 0.25rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow:
      0 8px 20px rgba(0, 0, 0, 0.16),
      0 2px 6px rgba(0, 0, 0, 0.08);
    outline: none;
  }

  .popover-menu.picker {
    width: 16rem;
    padding: 0;
    overflow: hidden;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    height: 1.75rem;
    padding: 0 0.5rem;
    font-family: inherit;
    font-size: 0.75rem;
    color: #374151;
    background: none;
    border: none;
    border-radius: 0.3125rem;
    text-align: left;
    cursor: pointer;
    white-space: nowrap;
  }

  .menu-item :global(.icon) {
    color: #6b7280;
  }

  .menu-item:hover,
  .menu-item:focus-visible {
    background: #f3f4f6;
  }

  .menu-item :global(.menu-trail) {
    margin-left: auto;
    color: #9ca3af;
  }

  .menu-item.danger,
  .menu-item.danger :global(.icon) {
    color: #dc2626;
  }

  .menu-item.danger:hover {
    background: #fef2f2;
  }

  .menu-divider {
    height: 1px;
    margin: 0.25rem 0.125rem;
    background: #f3f4f6;
  }

  /* Match RecentMoveMenuItem to this menu's scale. */
  .recent-move-slot {
    --menu-item-font-size: 0.75rem;
    --menu-item-padding: 0.375rem 0.5rem;
    --menu-item-gap: 0.5rem;
    --menu-item-radius: 0.3125rem;
  }

  /* ── Bottom sheet (mobile) ──────────────────────────────
     Same content as the popover, at touch scale: 44px+ targets, 16px+ inputs. */
  .task-detail.sheet {
    outline: none;
  }

  .sheet .popover-top {
    gap: 0;
    padding: 0 0.5rem 0 1rem;
  }

  .sheet .project-crumb {
    max-width: none;
    height: var(--mobile-tap);
    margin-left: -0.5rem;
    padding: 0 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 0.5rem;
  }

  .sheet .crumb-emoji {
    font-size: 0.875rem;
  }

  .sheet .icon-btn {
    width: var(--mobile-tap);
    height: var(--mobile-tap);
    border-radius: 999px;
  }

  .sheet .close-btn {
    color: #4b5563;
  }

  .sheet .task-main {
    grid-template-columns: 1.5rem 1fr;
    column-gap: 0.75rem;
    padding: 0.25rem 1rem 0.75rem;
  }

  .sheet .complete-check {
    position: relative;
    width: 1.5rem;
    height: 1.5rem;
    margin-top: 0.3125rem;
  }

  /* 44px hit area around the 24px circle */
  .sheet .complete-check::after {
    content: '';
    position: absolute;
    inset: -0.625rem;
  }

  .sheet .title-input {
    font-size: 1.25rem;
    line-height: 1.3;
    max-height: 8rem;
  }

  .sheet .notes-input {
    font-size: 1rem;
  }

  .sheet .task-props {
    grid-template-columns: 5.25rem 1fr;
    align-items: stretch; /* label and value cells share each row's divider */
    row-gap: 0;
    margin: 0;
    padding: 0 1rem;
  }

  .sheet .prop-label,
  .sheet .prop-value,
  .sheet .priority-picker {
    min-height: 3.25rem;
    border-top: 1px solid #f3f4f6;
  }

  .sheet .prop-label {
    font-size: 0.875rem;
  }

  /* Segmented priority: four equal 44px buttons */
  .sheet .priority-picker {
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
  }

  .sheet .priority-option {
    flex: 1;
    min-width: 0;
    justify-content: center;
    height: var(--mobile-tap);
    padding: 0 0.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    border-color: #e5e7eb;
    border-radius: 0.625rem;
  }

  .sheet .prop-value {
    padding: 0.375rem 0;
  }

  /* Tags: bigger chips, remove × with a padded hit area */
  .sheet .prop-value :global(.rounded-full.border) {
    min-height: 2rem;
    padding: 0 0.625rem;
    font-size: 0.875rem;
  }

  .sheet .prop-value :global(button[aria-label^='Remove tag']) {
    position: relative;
    margin-left: 0.25rem;
    font-size: 1rem;
  }

  .sheet .prop-value :global(button[aria-label^='Remove tag'])::after {
    content: '';
    position: absolute;
    inset: -0.875rem -0.75rem;
  }

  .sheet .prop-value :global(input) {
    height: var(--mobile-tap);
  }

  /* Now: full-row switch */
  .sheet .prop-value :global(.switch-toggle) {
    flex-direction: row-reverse;
    justify-content: space-between;
    width: 100%;
    min-height: var(--mobile-tap);
    gap: 0.75rem;
    padding: 0;
    text-align: left;
  }

  .sheet .prop-value :global(.switch) {
    width: 3.1875rem;
    height: 1.9375rem;
  }

  .sheet .prop-value :global(.switch::after) {
    top: 0.125rem;
    left: 0.125rem;
    width: 1.6875rem;
    height: 1.6875rem;
  }

  .sheet .prop-value :global(.switch-toggle.in-now .switch::after) {
    transform: translateX(1.25rem);
  }

  .sheet .prop-value :global(.switch-label) {
    font-size: 0.9375rem;
    font-weight: 500;
  }

  /* Working on: full-row switch (same look as the Now switch above) */
  .working-on-switch {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: var(--mobile-tap);
    gap: 0.75rem;
    padding: 0;
    font: inherit;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
  }

  .working-on-switch:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  .working-on-track {
    position: relative;
    flex-shrink: 0;
    width: 3.1875rem;
    height: 1.9375rem;
    border-radius: 999px;
    background: #d1d5db;
    transition: background-color 0.15s;
  }

  .working-on-track::after {
    content: '';
    position: absolute;
    top: 0.125rem;
    left: 0.125rem;
    width: 1.6875rem;
    height: 1.6875rem;
    border-radius: 999px;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    transition: transform 0.15s ease-out;
  }

  .working-on-switch.on .working-on-track {
    background: #f59e0b;
  }

  .working-on-switch.on .working-on-track::after {
    transform: translateX(1.25rem);
  }

  .working-on-label {
    font-size: 0.9375rem;
    font-weight: 500;
    color: #6b7280;
  }

  .working-on-switch.on .working-on-label {
    color: #b45309;
  }

  .prop-emoji {
    font-size: 0.8125rem;
    line-height: 1;
  }

  /* Primary actions */
  .sheet-actions {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
    padding: 0.75rem 1rem 0.625rem;
    border-top: 1px solid #f3f4f6;
  }

  .sheet-action {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.125rem;
    min-width: 0;
    height: 3.5rem;
    font-family: inherit;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #4b5563;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .sheet-action:active {
    background: #f1f5f9;
  }

  .sheet-action.danger {
    color: #dc2626;
  }

  .sheet-action:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .sheet .popover-footer {
    padding: 0 1rem 0.75rem;
    border-top: none;
  }

  .sheet .meta {
    font-size: 0.78125rem;
  }

  /* Menu and project search render inline, in place of the task body */
  .sheet .popover-menu,
  .sheet .popover-menu.picker {
    width: auto;
    padding: 0 0.5rem 0.5rem;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .sheet .popover-menu.picker {
    padding: 0 0 0.5rem;
  }

  .sheet .menu-item {
    height: 3rem;
    gap: 0.75rem;
    padding: 0 0.75rem;
    font-size: 1rem;
    border-radius: 0.5rem;
  }

  .sheet .popover-menu :global(.copy-row) {
    min-height: 3rem;
    padding: 0 0.75rem;
  }

  .sheet .popover-menu :global(.copy-row-btn) {
    min-height: 2.25rem;
    padding: 0 0.625rem;
  }

  .sheet .recent-move-slot {
    --menu-item-font-size: 1rem;
    --menu-item-padding: 0.875rem 0.75rem;
    --menu-item-gap: 0.75rem;
    --menu-item-radius: 0.5rem;
  }
</style>
